import './style.css';
import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash'
import CircuitTableRow from './circuit-table-row';
import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { getAllCircuits, getConfigItem } from 'util/device';
import {
  confirm,
  useNotificationsStore,
  RichTable,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import useAlarmsStore from 'store/alarms';
import useDevicesStore from 'store/devices';
import api from 'util/api';


/**
 * @function CircuitTable
 * @memberof module:CircuitTable
 *
 * @param {object} props
 * @param {object} props.device Device model
 * @returns {React.ReactElement}
 *
 * @example
 * <CircuitTable device={device} />
 */
function CircuitTable({device, includeControllerRows = false}) {
  const searchInput = useRef(null);
  const alarmSelect = useRef(null);
  const devices = getAllCircuits(device.model, includeControllerRows);
  const [filteredDevices, setFiltered] = useState(devices);
  const [selectedCircuits, setSelectedCircuits] = useState([]);
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [, devicesStoreActions] = useDevicesStore();
  const [, notificationsStoreActions] = useNotificationsStore();
  const [alarmStore, alarmsStoreActions] = useAlarmsStore();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    alarmsStoreActions.query();
  }, []);

  /**
   * Lock Out N number of circuits
   * @function lockOut
   * @memberof module:view/Alarms
   * @param {array} circuits an array of circuit models
   */
  function lockOut(circuits) {
    confirm({
      header: `${t`Are you sure you want to toggle lock out`} ${plural(circuits.length, {
        one: "# circuit",
        other: "# circuits",
      })}?`,
      message: t`Locking out circuits does not guarantee safety and will only suppress alarms.`
    }).then(async () => {
      setProcessing(true);
      const promise = Promise.all(circuits.map((circuit, i) => {
        const current = getConfigItem(circuit, 'LockoutTagOut')?.value;
        // NOTE - this ids kind of hacky but the api wrapper returns errors which
        // will result in the all rejecting to the catch block
        return new Promise((resolve, reject) => {
          api(`/devices/${circuit.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              type: 'Circuit',
              configuration: [{
                key: 'LockoutTagOut',
                value: current == 'on' ? 'off' : 'on'
              }]
            })
          })
          .then(res => resolve(res))
          .catch(res => resolve(res));
        });
      }));

      promise
        .then(values => {
          setSelectedCircuits([]);
          if (values.some(res => !res.ok)) {
            notificationsStoreActions.notify(
              t`Lock out request was partially successful. Please check the status of each circuit.`,
              'warning'
            );
          } else {
            notificationsStoreActions.notify(
              t`Lock out request was successful.`,
              'ok'
            );
          }
        })
        .catch(() => {
          notificationsStoreActions.notify(
            t`Lock out request failed.`,
            'alert'
          );
        })
        .finally(() => { // update no matter what the outcome is
          devicesStoreActions.query();
          setProcessing(false);
        });
    }).catch(() => {
      /* no-op */
    });
  }

  // Handle user search
  const updateSearch = debounce(() => {
    const searchTerm = (searchInput?.current?.value || '').toLowerCase();
    const alarmFilter = alarmSelect?.current?.value;
    if (!searchTerm && !alarmFilter) {
      setFiltered(devices);
      return;
    }
    let searchResults = devices.filter(device => {
      if (device.type == 'Controller') return false;
      return device.name.toLowerCase().includes(searchTerm);
    });

    if (alarmFilter) {
      searchResults = searchResults.filter((device) => {
        if (device.type == 'Controller') return true;
        const hasAlarms = alarmStore.alarms.some(alarm => {
          return alarm.deviceId == device.id;
        });
        return alarmFilter == 'active' ? hasAlarms : !hasAlarms;
      });
    }

    setFiltered(searchResults);
  }, 200);

  const columns = [
    t`Circuit`,
    t`Temperature`,
    t`Current`,
    t`Ground Current`,
    t`Power Delivery`,
  ];

  return (<>
    <div className="table-controls">
      <span className="search-input">
        <input
          ref={searchInput}
          type="text"
          className="search"
          placeholder={t`Search Circuits`}
          onChange={updateSearch}
        />
      </span>
      <select onChange={updateSearch} ref={alarmSelect}>
        <option value={''}><Trans>Alarm Status</Trans></option>
        <option value={'active'}><Trans>Active</Trans></option>
        <option value={'ok'}><Trans>OK</Trans></option>
      </select>
      {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <button
        className={`lock-out ${processing ? 'processing' : ''}`}
        onClick={() => lockOut(selectedCircuits)}
        disabled={!selectedCircuits.length || processing}
        data-tooltip-content={plural(selectedCircuits.length, {
          one: 'Toggle Lock Out / Tag Out for # Circuit',
          other: 'Toggle Lock Out / Tag Out for # Circuits',
        })}
        data-tooltip-id="circuit-table-tooltip"
      >
        <Trans>Lock Out / Tag Out</Trans>
      </button> : null}
      {/*
      <button
        className="force-on"
        disabled={!selectedCircuits.length || processing}
        data-tooltip-content={t`Force On`}
        data-tooltip-id="circuit-table-tooltip"
      >
        <Trans>Force On</Trans>
      </button>
      */}
      <Tooltip id="circuit-table-tooltip" place="top" />
    </div>
    <RichTable
      columns={columns}
      rows={filteredDevices}
      allowSelection={usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles)}
      responsive={true}
      onChange={selected => setSelectedCircuits(selected.filter(row => row.type != 'Controller'))}
      className="circuit-table"
    >
      <CircuitTableRow />
    </RichTable>
  </>)
}

export default CircuitTable;
